/**
 * FlowchartDetailPage — `/flowcharts/:id`.
 *
 * Show metadata + raw mermaid source for a single flowchart. Mermaid runtime
 * is not wired — source renders as a `<pre>` block.
 */
import type { JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowRight, FileText, Server, Users } from 'lucide-react';

import { PageHeader } from '../shared/StaffUI';
import { MODULES, getFlowchartById } from './flowchartRegistry';

interface MetaCardProps {
  readonly icon: typeof Users;
  readonly title: string;
  readonly items: readonly string[];
}

function MetaCard({ icon: Icon, title, items }: MetaCardProps): JSX.Element {
  return (
    <div className="bg-white border border-border rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-3.5 h-3.5 text-muted" />
        <p className="text-[10px] font-bold text-muted uppercase tracking-wider">{title}</p>
      </div>
      <ul className="space-y-1">
        {items.map((item) => (
          <li key={item} className="text-xs text-gray-700 flex items-start gap-2">
            <span className="text-nipost-green mt-0.5">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function FlowchartDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const fc = id ? getFlowchartById(id) : undefined;

  if (!fc) {
    return (
      <div className="max-w-2xl">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-gray-900">Flowchart not found</p>
            <p className="text-xs text-muted mt-1">
              No diagram registered for id <code className="bg-white px-1 rounded">{id}</code>.
            </p>
            <Link
              to="/flowcharts"
              className="inline-flex items-center gap-1 text-xs text-nipost-green font-semibold mt-3 hover:underline"
            >
              Back to all flowcharts
              <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const moduleLabel = MODULES.find((m) => m.slug === fc.module)?.label ?? fc.module;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-bold text-nipost-green uppercase tracking-widest mb-2">
          {moduleLabel}
        </p>
        <PageHeader title={fc.title} description={fc.description} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {fc.featureIds.map((fid) => (
          <span
            key={fid}
            className="px-2 py-1 text-[10px] font-mono font-semibold bg-nipost-green/10 text-nipost-green rounded"
          >
            {fid}
          </span>
        ))}
      </div>

      <div className="bg-white border border-border rounded-2xl p-6 overflow-x-auto">
        <pre className="text-xs font-mono text-gray-700 whitespace-pre">{fc.mermaid}</pre>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {fc.actors.length > 0 ? <MetaCard icon={Users} title="Actors" items={fc.actors} /> : null}
        {fc.systems.length > 0 ? <MetaCard icon={Server} title="Systems" items={fc.systems} /> : null}
      </div>

      <div className="flex items-center gap-2 text-[11px] text-muted">
        <FileText className="w-3 h-3" />
        <span>
          Diagrams curated in{' '}
          <code>apps/web-staff/src/features/flowcharts/flowchartRegistry.ts</code>.
        </span>
      </div>
    </div>
  );
}
